import {
  AnalyticsIsEnabled,
  AnalyticsApiKey,
  GTMSrcID,
  GTMFrameID,
} from '@app/data';
import { AppState } from '@app/interfaces';
import { StepsTypes } from '@app/pages/enums';
import { FormData } from '@app/pages/types';
import { getContainerNode, logMetric, logger, Metric } from '@app/utils';

export enum EventType {
  FormComplete = 'drvtrd_form_complete',
  FormStart = 'drvtrd_form_start',
  Next = 'drvtrd_next',
  OfferView = 'drvtrd_offer_view',
  ResendPhotosLink = 'drvtrd_resend_photos_link',
  TakePhotos = 'drvtrd_take_photos',
}

enum PricingEngine {
  Acv = 'acv',
}

enum SourceScreen {
  ContactScreen = 'contact-screen',
  SMSScreen = 'sent-sms-screen',
}

enum WidgetWorkflow {
  Disclosure = 'disclosure',
  Inspection = 'inspection',
}

interface GTMFormPayload {
  color: string;
  dateAdded: string;
  make: string;
  mileage: string;
  mode: string;
  model: string;
  offer: number;
  offerViewClickSource: string;
  phone: number;
  plate: string;
  pricingEngine: string;
  series: string;
  state: string;
  stepNumber: number;
  storeId: string;
  style: string;
  vehicleId: number;
  vin: string;
  workflow: string;
  year: string;
  zipCode: number;
}

interface GTMTypes {
  logApiBody: string;
  logApiMethod: string;
  logApiParams: string;
  logApiResBody: string;
  logApiResStatus: number;
  logApiResStatusText: string;
  logApiResUrl: string;
  logApiUrl: string;
  logMessage: string;
  logType: string;
}

export interface LogEventProps {
  body: string | object;
  message: string | object;
  method: string;
  params: string;
  resBody: string;
  resStatus: number;
  resStatusText: string;
  resUrl: string;
  type: string;
  url: string;
}

export const initAnalytics = (): void => {
  if (AnalyticsIsEnabled) {
    // Add GTM script
    const analyticsNode = getContainerNode(`${GTMSrcID}`);
    if (!analyticsNode) {
      (function (w, d, s, l, i) {
        w.dataLayer = w.dataLayer || [];
        w.dataLayer.push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js',
        });

        const f = d.getElementsByTagName(s)[0],
          j = d.createElement(s) as HTMLScriptElement,
          dl = l != 'dataLayer' ? `&l=${l}` : '';

        j.id = GTMSrcID;
        j.async = true;
        j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;

        f.parentNode?.insertBefore(j, f);
      })(window, document, 'script', 'dataLayer', AnalyticsApiKey);
    }

    // Add GTM iframe
    const gtmIframe = getContainerNode(`${GTMFrameID}`);
    if (!gtmIframe) {
      const iframe = document.createElement('iframe') as HTMLIFrameElement;

      iframe.id = GTMFrameID;
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${AnalyticsApiKey}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.setAttribute('style', 'display:none;visibility:hidden');

      document.body.prepend(iframe);
    }
  }
};

export const analyticsFireEvent = (
  event: string,
  data: Partial<GTMFormPayload> | Partial<GTMTypes> = {}
): void => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ...data, event });
};

export const stringifyParams = (obj: object | string): string | object => {
  try {
    if (typeof obj === 'object') {
      return JSON.stringify(obj);
    }
    return obj;
  } catch (e) {
    logger.error('Failed to stringify', e as Partial<unknown>);
    return obj;
  }
};

export const getWorkflow = (isInspectionOffer = false) =>
  isInspectionOffer ? WidgetWorkflow.Inspection : WidgetWorkflow.Disclosure;

export const getCurrentDate = () => {
  const now = new Date();
  const utcMonth = now.getMonth() + 1;
  const utcDay = now.getDate();
  const utcYear = now.getFullYear();
  const formattedDate = `${utcMonth}/${utcDay}/${utcYear}`;

  return formattedDate;
};

export const handleGTMEvents = (
  currentStep: number,
  stepData: FormData,
  appState: Partial<AppState>
) => {
  const selectedVehicleAttributes = appState?.ui?.navigation?.selectedVehicle;
  const domAttributes = appState?.ui?.domAttributes;
  const navigationAttributes = appState?.ui?.navigation;
  const vehicleAddedAttributes = appState?.vehicle?.vehicleAdded?.data;
  const vehiclePricingAttrs = appState?.vehicle?.acvPricing?.data;
  const isInspectionOffer = domAttributes?.isInspectionOffer as boolean;

  // Start from index 1 as required for analytics
  const stepNumber = currentStep + 1;

  switch (currentStep) {
    case StepsTypes.VehicleSelection:
      logMetric(Metric.FormStart);

      const formStartPayload: Partial<GTMFormPayload> = {
        make: selectedVehicleAttributes?.make,
        mode: stepData?.mode,
        model: selectedVehicleAttributes?.model,
        plate: stepData?.plate,
        series: stepData?.trim,
        state: stepData?.state,
        stepNumber,
        storeId: domAttributes?.storeId,
        style: selectedVehicleAttributes?.style,
        vin: navigationAttributes?.vin as string,
        workflow: getWorkflow(isInspectionOffer),
        year: selectedVehicleAttributes?.year as string,
      };

      analyticsFireEvent(EventType.FormStart, formStartPayload);

      break;
    case StepsTypes.CustomerInfo:
      logMetric(Metric.FormComplete);

      const formCompletePayload: Partial<GTMFormPayload> = {
        color: stepData?.color,
        dateAdded: getCurrentDate(),
        mileage: stepData?.odometer,
        make: selectedVehicleAttributes?.make,
        mode: stepData?.mode,
        model: selectedVehicleAttributes?.model,
        offer: (vehiclePricingAttrs?.price as number) || 0,
        plate: stepData?.plate,
        phone: stepData?.phone,
        series: stepData?.trim,
        state: stepData?.state,
        stepNumber,
        storeId: domAttributes?.storeId,
        style: selectedVehicleAttributes?.style,
        vehicleId:
          vehicleAddedAttributes?.vehicleAdded ||
          vehicleAddedAttributes?.vehicleExists,
        vin: navigationAttributes?.vin as string,
        workflow: getWorkflow(isInspectionOffer),
        year: selectedVehicleAttributes?.year,
        zipCode: navigationAttributes?.isZipValid?.zipCode,
      };

      analyticsFireEvent(EventType.FormComplete, formCompletePayload);

      break;
    default:
      const nextEventPayload: Partial<GTMFormPayload> = {
        make: selectedVehicleAttributes?.make,
        mileage: stepData?.odometer,
        mode: stepData?.mode,
        model: selectedVehicleAttributes?.model,
        plate: stepData?.plate,
        series: stepData?.trim,
        state: stepData?.state,
        stepNumber,
        storeId: domAttributes?.storeId,
        style: selectedVehicleAttributes?.style,
        vin: navigationAttributes?.vin as string,
        workflow: getWorkflow(isInspectionOffer),
        year: selectedVehicleAttributes?.year,
        zipCode: navigationAttributes?.isZipValid?.zipCode,
      };

      analyticsFireEvent(EventType.Next, nextEventPayload);

      break;
  }
};

export const trackOfferViewEvent = (
  stepData: FormData,
  appState: Partial<AppState>,
  currentStep: number,
  isFromContactInfoScreen = false
) => {
  const selectedVehicleAttributes = appState?.ui?.navigation?.selectedVehicle;
  const domAttributes = appState?.ui?.domAttributes;
  const navigationAttributes = appState?.ui?.navigation;
  const vehicleAddedAttributes = appState?.vehicle?.vehicleAdded?.data;
  const vehiclePricingAttrs = appState?.vehicle?.acvPricing?.data;
  const isInspectionOffer = domAttributes?.isInspectionOffer as boolean;
  const offerViewClickSource = isFromContactInfoScreen
    ? SourceScreen.ContactScreen
    : SourceScreen.SMSScreen;

  // Start from index 1 as required for analytics
  const stepNumber = currentStep + 1;

  const offerViewPayload: Partial<GTMFormPayload> = {
    make: selectedVehicleAttributes?.make,
    mileage: stepData?.odometer,
    model: selectedVehicleAttributes?.model,
    offer: vehiclePricingAttrs?.price as number,
    offerViewClickSource,
    plate: stepData?.plate,
    pricingEngine: PricingEngine.Acv,
    series: stepData?.trim,
    state: stepData?.state,
    stepNumber,
    storeId: domAttributes?.storeId,
    style: selectedVehicleAttributes?.style,
    vehicleId:
      vehicleAddedAttributes?.vehicleAdded ||
      vehicleAddedAttributes?.vehicleExists,
    vin: navigationAttributes?.vin as string,
    workflow: getWorkflow(isInspectionOffer),
    year: selectedVehicleAttributes?.year,
    zipCode: navigationAttributes?.isZipValid?.zipCode,
  };

  analyticsFireEvent(EventType.OfferView, offerViewPayload);
};
